import { ALLOWED_IMAGE_MIME_TYPES } from '@/services/assetLibrary';

/**
 * SVG 偵測 + 檔案選擇器的 accept(R415)
 * -----------------------------------------------------------------------------
 * R415 起「圖片上傳的地方都收 SVG」—— 但**上傳鏈本身仍然不收**:SVG 一律先在瀏覽器轉成
 * PNG / JPG(使用者確認後),上傳的永遠是轉好的點陣圖。所以這裡有兩份清單,不可以合併:
 *
 *   - {@link ALLOWED_IMAGE_MIME_TYPES}(services 層)= **上傳白名單**,不含 SVG;
 *     `validateImageFile` 與 BE `GCS_*_ALLOWED_MIME_TYPES` 都照它擋 —— 轉檔漏接時的最後防線。
 *   - {@link IMAGE_PICKER_ACCEPT}(本檔)= **檔案選擇器看得到什麼**,多了 SVG。
 *     只能給「選檔之後會先走 `prepareImageUpload`」的入口用(`svgUploadGate.test` 機械鎖)。
 *
 * ## 偵測判準(三擇一即算 SVG)
 * 1. 宣告的 mime 是 `image/svg+xml`
 * 2. 副檔名 `.svg`(拖放時 `file.type` 可能是空字串)
 * 3. **內容**看起來是 SVG —— 例:`logo.png` 其實是 SVG 文字。以前這種檔會被 magic byte
 *    擋下來、顯示「檔案內容與宣告格式不符」;現在一樣走轉檔確認,使用者看得到它其實是什麼。
 *
 * ⚠️ 影片(`video/*`)不讀內容:影片檔動輒數十 MB,而它不可能是 SVG。
 */

export const SVG_MIME = 'image/svg+xml';

/** 檔案選擇器 accept:上傳白名單 + SVG(SVG 選進來會先轉檔,不會直接上傳)。 */
export const IMAGE_PICKER_ACCEPT = [...ALLOWED_IMAGE_MIME_TYPES, SVG_MIME, '.svg'].join(',');

/** 內容嗅探只讀檔頭這麼多 —— Illustrator 匯出的註解 + DOCTYPE 通常不到 1 KB。 */
const SNIFF_BYTES = 4096;

/**
 * 檔頭文字是不是 SVG 文件的開頭:允許 BOM、空白、XML 宣告、註解、DOCTYPE(含內部子集
 * `[...]`,Illustrator 的 `<!ENTITY ...>` 就寫在裡面),之後第一個元素必須是 `<svg`。
 */
const SVG_HEAD =
  /^\s*(?:<\?xml[^>]*\?>\s*)?(?:(?:<!--[\s\S]*?-->|<!DOCTYPE[^[>]*(?:\[[\s\S]*?\])?\s*>)\s*)*<svg[\s>/]/i;

export function looksLikeSvgText(head: string): boolean {
  return SVG_HEAD.test(head.replace(/^\uFEFF/, ''));
}

/** 同步判準(只看宣告:mime / 副檔名)—— 給「一般圖片必須維持同步」的入口用(封面)。 */
export function isDeclaredSvg(file: File): boolean {
  return file.type === SVG_MIME || /\.svg$/i.test(file.name);
}

/**
 * 這個檔案要不要走 SVG 轉檔。讀檔失敗一律回 false(交給原本的驗證鏈處理,不在這裡擋人)。
 */
export async function isSvgFile(file: File): Promise<boolean> {
  if (isDeclaredSvg(file)) return true;
  if (file.type.startsWith('video/')) return false;
  try {
    const bytes = new Uint8Array(await file.slice(0, SNIFF_BYTES).arrayBuffer());
    // 點陣圖 magic byte 的第一個 byte 不會是 `<` / 空白 / BOM —— 先用一個 byte 快篩,
    // 不必為每張 PNG / JPG 解一次文字。
    const first = bytes[0];
    if (first === undefined) return false;
    const textual = first === 0x3c || first === 0xef || first === 0x20 || first === 0x09 || first === 0x0a || first === 0x0d;
    if (!textual) return false;
    return looksLikeSvgText(new TextDecoder('utf-8', { fatal: false }).decode(bytes));
  } catch {
    return false;
  }
}
